import { useState, useEffect, useCallback } from "react";

interface WizardRecord<T> {
  state: T;
  savedAt: string; // ISO string
}

/**
 * Generic hook for persisting wizard state in localStorage so users can
 * leave mid-wizard (e.g. to create a required resource) and resume later.
 *
 * Usage:
 *   const { wizardState, setWizardState, clearWizardState,
 *           isResuming, resumeWizard, startFresh } = useWizardState("routing-wizard", initialState);
 *
 * - Call setWizardState() whenever wizard state changes — it auto-saves.
 * - When the wizard mounts and finds saved state, isResuming = true.
 * - Show a resume banner and call resumeWizard() or startFresh() based on user choice.
 * - Call clearWizardState() when the wizard completes or is cancelled.
 */
export default function useWizardState<T extends object>(
  /** Unique key for this wizard (e.g. "routing-wizard") */
  wizardKey: string,
  /** Initial state when starting fresh */
  initial: T,
  /** How many days to keep saved state before auto-expiring (default 7) */
  expiryDays: number = 7
) {
  const storageKey = `wizard_state_${wizardKey}`;

  function readSaved(): WizardRecord<T> | null {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const record = JSON.parse(raw) as WizardRecord<T>;
      const ageMs = Date.now() - new Date(record.savedAt).getTime();
      if (ageMs > expiryDays * 86_400_000) {
        localStorage.removeItem(storageKey);
        return null;
      }
      return record;
    } catch {
      return null;
    }
  }

  const [wizardState, _setRaw] = useState<T>(initial);
  const [resumeRecord] = useState<WizardRecord<T> | null>(() => readSaved());
  const [isResuming, setIsResuming] = useState(false);

  // On mount: if there's a saved record, enter resume-prompt mode
  useEffect(() => {
    if (resumeRecord) setIsResuming(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Update wizard state and auto-save to localStorage */
  const setWizardState = useCallback(
    (updater: T | ((prev: T) => T)) => {
      _setRaw((prev) => {
        const next =
          typeof updater === "function"
            ? (updater as (p: T) => T)(prev)
            : updater;
        try {
          localStorage.setItem(
            storageKey,
            JSON.stringify({ state: next, savedAt: new Date().toISOString() })
          );
        } catch {}
        return next;
      });
    },
    [storageKey]
  );

  /** Restore the saved state and dismiss the resume prompt */
  const resumeWizard = useCallback(() => {
    if (resumeRecord) _setRaw(resumeRecord.state);
    setIsResuming(false);
  }, [resumeRecord]);

  /** Discard saved state and start over */
  const startFresh = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch {}
    _setRaw(initial);
    setIsResuming(false);
  }, [storageKey, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Call this when the wizard completes or is cancelled */
  const clearWizardState = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch {}
    setIsResuming(false);
  }, [storageKey]);

  function formatSavedAt(iso: string) {
    const d = new Date(iso);
    const now = new Date();
    const diffH = (now.getTime() - d.getTime()) / 3_600_000;
    if (diffH < 1) return "just now";
    if (diffH < 24)
      return `today at ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString())
      return `yesterday at ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  }

  return {
    wizardState,
    setWizardState,
    clearWizardState,
    isResuming,
    /** Friendly string like "today at 2:31 PM" for use in the resume banner */
    resumedFromLabel: resumeRecord ? formatSavedAt(resumeRecord.savedAt) : null,
    resumeWizard,
    startFresh,
  };
}
